// Skelett der Kopfkarte (ST-UX-001; NFR-UX-004, NFR-ACC-001 1.4.4/1.4.10/2.3.3).
//
// Das Skelett ist die Kopfkarte selbst: dieselben Klassen (`useKopfStile`), mit
// Platzhaltern statt Inhalt. Hoehe und Umbruch folgen so bei jeder Breite und
// jedem Textzoom denselben Regeln wie der Kopf; es gibt keine Stufentabelle, die
// nach jeder Kopfaenderung neu gemessen werden muesste (Probe B76).
//
// Die Platzhalter sind Text in der echten Typografie, unsichtbar gefaerbt und mit
// Flaeche hinterlegt. Der ganze Teil ist `aria-hidden`; gehoert wird allein der
// Ladesatz des umgebenden `Skeleton` (shell/skelett.tsx). Keine Landmarken,
// Ueberschrift oder Liste: `div`/`span` mit den Klassen des Kopfs.

import type { ReactElement, ReactNode } from 'react';
import { Body1, Caption1, Card, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

import { StatusPille } from '../../shell/status-pille.tsx';
import { useKopfStile } from './kopf.tsx';

// Texte eines typischen aktiven Kontakts mit Sichtbarkeits-Badge, in der Laenge
// des Referenzkontakts der Probe B76. Andere Kontakte weichen ab (gemessen bis
// +68/-64 px: langer Organisationsname, nicht beschreibbarer Kontakt ohne
// Knoepfe); vor dem Laden ist nichts davon bekannt.
const NAME = 'Erika Probestatus';
const HAUS = 'Nur für Verlag Nord sichtbar';
const WIEDERVORLAGE = '1 offene Wiedervorlage';
const EMAIL = 'erika.probestatus@example.org';

const useStyles = makeStyles({
  // Flaeche statt Inhalt: der Text bleibt im Fluss (Breite, Umbruch), ist aber nicht lesbar.
  platzhalter: {
    color: 'transparent',
    backgroundColor: tokens.colorNeutralStencil1,
    borderRadius: tokens.borderRadiusMedium,
    boxDecorationBreak: 'clone',
    userSelect: 'none',
    // Erzwungene Farben ueberschreiben `transparent`; ohne `forced-color-adjust` stuende der
    // Platzhaltertext als lesbarer Fake-Inhalt auf der Flaeche.
    '@media (forced-colors: active)': { forcedColorAdjust: 'none', color: 'GrayText', backgroundColor: 'GrayText' },
  },
  pfad: { display: 'block', lineHeight: tokens.lineHeightBase200 },
  symbolFlaeche: {
    backgroundColor: tokens.colorNeutralStencil1,
    '@media (forced-colors: active)': { backgroundColor: 'GrayText' },
  },
  // Badge: Rand und Fuellung des echten Badges weichen der Platzhalterflaeche.
  badge: {
    color: 'transparent',
    backgroundColor: tokens.colorNeutralStencil1,
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
    userSelect: 'none',
    '@media (forced-colors: active)': { forcedColorAdjust: 'none', color: 'GrayText', backgroundColor: 'GrayText' },
  },
  // Wie `Button` (medium): 32 px hoch, 12 px Innenabstand, Symbolplatz 20 px, Rand 1 px.
  knopf: {
    display: 'inline-flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    minHeight: '32px',
    paddingBlock: '5px',
    paddingInline: '12px',
    border: '1px solid transparent',
    columnGap: '6px',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    // Wie beim Button bricht der Text um, statt die Karte zu weiten; das Symbol gibt dann nach.
    maxWidth: '100%',
    textAlign: 'center',
  },
  symbolKnopf: { width: '32px', paddingInline: 0, justifyContent: 'center' },
  zeichenplatz: { width: '20px', height: '20px' },
  kleinPlatz: { width: '16px', height: '16px', flexShrink: 0 },
  kopierplatz: { width: '24px', height: '24px', flexShrink: 0 },
});

function Knopf({ text }: { readonly text?: string }): ReactElement {
  const eigen = useStyles();
  return (
    <span className={mergeClasses(eigen.knopf, text === undefined ? eigen.symbolKnopf : undefined)}>
      <span className={eigen.zeichenplatz} />
      {text === undefined ? undefined : <span className={eigen.platzhalter}>{text}</span>}
    </span>
  );
}

function Wert({ children }: { readonly children: ReactNode }): ReactElement {
  const own = useKopfStile();
  const eigen = useStyles();
  return (
    <div className={own.wert}>
      <span className={eigen.kleinPlatz} />
      <Body1 className={eigen.platzhalter}>{children}</Body1>
      <span className={eigen.kopierplatz} />
    </div>
  );
}

/** Die Kopfkarte als Platzhalter; gehoert in ein `Skeleton`, das den Ladesatz traegt. */
export function KopfSkelett(): ReactElement {
  const own = useKopfStile();
  const eigen = useStyles();
  return (
    <Card className={own.karte} data-skelett="kopf" aria-hidden="true">
      <span className={mergeClasses(own.symbol, eigen.symbolFlaeche)} />
      <div className={own.inhalt}>
        <Caption1 className={eigen.pfad}>
          <span className={eigen.platzhalter}>Kontakte / {NAME}</span>
        </Caption1>
        <div className={own.name}>
          <span className={eigen.platzhalter}>{NAME}</span>
        </div>
        <div className={own.zeile}>
          <StatusPille status="active" className={eigen.badge} />
          <Body1 className={eigen.platzhalter}>{HAUS}</Body1>
          <Body1 className={mergeClasses(own.fakt, own.wert)}>
            <span className={eigen.kleinPlatz} />
            <span className={eigen.platzhalter}>{WIEDERVORLAGE}</span>
          </Body1>
        </div>
        <div className={own.werte}>
          <Wert>{EMAIL}</Wert>
        </div>
      </div>
      <div className={own.rechts}>
        <div className={own.aktionen}>
          <Knopf text="Notiz" />
          <Knopf text="Wiedervorlage" />
          <Knopf />
        </div>
        <span />
      </div>
    </Card>
  );
}
